import type { MembershipSpend } from '@fruitful/member-profile/src/spend/types/Spend';
import {
    AccountCategory,
    type AccountSummary,
    type BillCategory,
    type CategorySummary,
} from '@fruitful/types';
import { atom } from 'jotai';

import { emptyBillNew } from '../../common/utils/billUtils';
import { holisticSummaryAtom } from '../../holistic/state/HolisticAtoms';
import {
    BillActivationStatus,
    type IBill,
    InlineEditableBillFields,
    type ManualBill,
    type PartialBill,
    type SelectableBill,
} from '../types/Bill';
import type { BillDiscoveryReviewAccount } from '../types/BillDiscoveryAccount';

const billsEligibleAccountCategories = [
    AccountCategory.SPEND,
    AccountCategory.SAVE,
];

// A "derived atom" that filters and flattens values from the holisticSummaryAtom
// Its value should be persisted but synced with the holisticSummaryAtom dependency, like useMemo
// TODO we should consider having a paired atom for holisticSummaryAtom that doesn't include the frequently updating data like balances.
export const billsAccountsAtom = atom<AccountSummary[]>((get) => {
    const holisticSummary = get(holisticSummaryAtom);
    const billAccounts = billsEligibleAccountCategories.flatMap(
        (category) =>
            (holisticSummary?.[category] as CategorySummary)?.accounts ?? [],
    );
    return billAccounts;
});

export const billDiscoveryReviewAccountsAtom = atom<
    BillDiscoveryReviewAccount[]
>([]);

// Spinwheel liabilities data
export interface Liability {
    id: string;
    type: string;
    creditor: string;
    accountNumber: string;
    balance: number;
    monthlyPayment: number;
    interestRate?: number;
    status: string;
    openedDate?: string;
    lastPaymentDate?: string;
    logoUrl?: string;
    reviewStatus?: string;
    // Credit card specific fields
    creditLimit?: number;
    availableCredit?: number;
    creditUtilization?: number;
    dueDate?: string;
}

// Credit Card specific data structure
export interface CreditCard {
    id: string;
    creditor: string;
    displayName: string;
    accountNumber: string;
    statementBalance: number;
    minimumPayment: number;
    creditLimit?: number;
    availableCredit?: number;
    creditUtilization?: number;
    interestRate?: number;
    dueDate?: string;
    lastPaymentDate?: string;
    openedDate?: string;
    status: string;
    logoUrl?: string;
    reviewStatus?: string;
}

interface LiabilitiesByType {
    creditCards: Liability[];
    autoLoans: Liability[];
    homeLoans: Liability[];
    personalLoans: Liability[];
    studentLoans: Liability[];
    miscellaneousLiabilities: Liability[];
}

export interface LiabilitiesResponseDto {
    totalMonthlyMinimum: number;
    totalOutstandingBalance: number;
    liabilities: LiabilitiesByType;
    reportDate: number;
    summary: {
        creditCards: unknown;
        autoLoans: unknown;
        homeLoans: unknown;
        personalLoans: unknown;
        studentLoans: unknown;
        miscellaneousLiabilities: unknown;
    };
}

export const spinwheelLiabilitiesAtom = atom<LiabilitiesResponseDto | null>(
    null,
);

// Credit card that is selected for viewing/editing
export const selectedCreditCardAtom = atom<CreditCard | null>(null);

export const billsToReviewAtom = atom<IBill[]>([]);

// The set of selected bills that should be submitted
export const selectedReviewBillsAtom = atom<SelectableBill[]>([]);

// Review bill that is selected for editing
export const selectedReviewBillAtom = atom<IBill | PartialBill | null>(null);
type BillReviewSource =
    | 'onboarding'
    | 'profile'
    | 'home-review'
    | 'home'
    | 'spinwheel-testing'
    | 'magic-numbers-onboarding';
export const billReviewSourceAtom = atom<BillReviewSource | null>(null);

export const activeBillsAtom = atom<IBill[]>([]);

export const allBillsAtom = atom<IBill[]>([]);
export const membershipSpendAtom = atom<MembershipSpend | null>(null);

export const activeAndReviewBillsAtom = atom((get) =>
    get(allBillsAtom).filter((bill) =>
        [BillActivationStatus.ACTIVE, BillActivationStatus.REVIEW].includes(
            bill.activationStatus,
        ),
    ),
);

export const selectedBillAtom = atom<IBill | PartialBill | null>(null);

export const selectedBillCategoriesAtom = atom<string[]>([]);

export const currentlyEditingBillFieldAtom =
    atom<InlineEditableBillFields | null>(InlineEditableBillFields.AMOUNT);

export const isEditingBillTransactionsAtom = atom<boolean>(false);

export const manualBillAtom = atom<ManualBill>(emptyBillNew());

export const addBillCategoryAtom = atom<BillCategory | null>(null);
